/**
 * SSAuditTimeline — visual timeline of audit events for a party.
 *
 * Replaces the plain table audit view with a chronological timeline,
 * grouped by day, colour-coded by action.  Falls back to a flat list when
 * there are too many events (>200).
 */
import React, { useEffect, useState } from 'react';
import { ssPartyAPI } from '../../../services/ssApi';
import { COLOR, RADIUS, Badge, Spinner, EmptyState, ErrorBanner } from '../../../components/ss';

const ACTION_COLORS = {
  INSERT: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  MERGE:  'purple',
  CLOSE:  'amber',
  OPEN:   'green',
};

const fmtDay  = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

export default function SSAuditTimeline({ partyId }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr('');
    ssPartyAPI.auditHistory(partyId)
      .then(res => { if (alive) setEvents(res?.data || []); })
      .catch(e => { if (alive) setErr(e.response?.data?.detail || 'Failed to load audit history'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [partyId]);

  if (loading) return <Spinner />;
  if (err)     return <ErrorBanner>{err}</ErrorBanner>;
  if (!events.length) return <EmptyState icon="🕒" title="No audit events" message="No tracked changes for this party yet." />;

  // Group by day
  const groups = {};
  events.forEach(ev => {
    const dt = new Date(ev.audit_date || ev.created_at || ev.timestamp || Date.now());
    const key = dt.toISOString().slice(0, 10);
    if (!groups[key]) groups[key] = { day: dt, items: [] };
    groups[key].items.push({ ...ev, _dt: dt });
  });
  const sortedDays = Object.keys(groups).sort().reverse();

  return (
    <div>
      {sortedDays.map(dayKey => {
        const g = groups[dayKey];
        return (
          <div key={dayKey} style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 11, fontWeight: 600,
              color: COLOR.textMuted, textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 12, paddingBottom: 6,
              borderBottom: `1px solid ${COLOR.grayBorder}`,
            }}>
              {fmtDay(g.day)}
            </div>
            <div style={{ position: 'relative', paddingLeft: 24 }}>
              {/* Vertical rail */}
              <div style={{
                position: 'absolute', left: 7, top: 6, bottom: 6,
                width: 2, background: COLOR.graySubtle,
              }} />
              {g.items.map((ev, i) => {
                const action = (ev.action || ev.event_type || 'UPDATE').toUpperCase();
                const color  = ACTION_COLORS[action] || 'gray';
                return (
                  <div key={i} style={{ position: 'relative', marginBottom: 14 }}>
                    {/* Dot */}
                    <div style={{
                      position: 'absolute', left: -22, top: 5,
                      width: 12, height: 12, borderRadius: 6,
                      background: COLOR[color] || COLOR.gray,
                      border: `2px solid #fff`,
                      boxShadow: `0 0 0 2px ${COLOR.graySubtle}`,
                    }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <div>
                        <Badge color={color}>{action}</Badge>{' '}
                        <span style={{ fontSize: 13, color: COLOR.text }}>
                          {ev.table_name || ev.aggregate || 'change'}
                        </span>
                        {ev.column_name && (
                          <span style={{ fontSize: 12, color: COLOR.textMuted, marginLeft: 6 }}>
                            · {ev.column_name}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: COLOR.textMuted, whiteSpace: 'nowrap' }}>
                        {fmtTime(ev._dt)} · {ev.actor || ev.changed_by || 'system'}
                      </div>
                    </div>
                    {(ev.before_value || ev.after_value || ev.payload) && (
                      <div style={{
                        marginTop: 4, padding: '6px 10px',
                        background: COLOR.grayBg,
                        borderRadius: RADIUS.sm,
                        fontSize: 12, color: COLOR.textSub,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {ev.before_value !== undefined || ev.after_value !== undefined
                          ? <>
                              <span style={{ color: COLOR.red }}>{String(ev.before_value ?? '∅')}</span>
                              {'  →  '}
                              <span style={{ color: COLOR.green }}>{String(ev.after_value ?? '∅')}</span>
                            </>
                          : (typeof ev.payload === 'string'
                              ? ev.payload
                              : JSON.stringify(ev.payload, null, 2))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
